/**
 * Chat message list — user + assistant messages
 */
import { useEffect, useRef } from 'react'
import type { ChatMessage } from './useAskRunAI'

interface Props {
  messages: ChatMessage[]
  loading: boolean
}

export default function MessageList({ messages, loading }: Props) {
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  if (messages.length === 0) {
    return (
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--os-dim)',
        fontSize: '13px',
        gap: '8px',
        padding: '20px',
      }}>
        <div style={{ fontSize: '32px' }}>⚡</div>
        <div style={{ fontWeight: 600, color: 'var(--os-muted)' }}>Ask RunAI anything</div>
        <div style={{ fontSize: '11px', textAlign: 'center', maxWidth: '280px' }}>
          512 agents ready. Use skill pills below or type your request.
        </div>
      </div>
    )
  }

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {messages.map((msg, i) => (
        <div key={i} style={{
          display: 'flex',
          flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
          gap: '8px',
          alignItems: 'flex-start',
        }}>
          {/* Avatar */}
          <div style={{
            width: '28px',
            height: '28px',
            borderRadius: '8px',
            background: msg.role === 'user'
              ? 'linear-gradient(135deg,#6366f1,#8b5cf6)'
              : 'linear-gradient(135deg,#ec4899,#f43f5e)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            fontWeight: 700,
            color: '#fff',
            flexShrink: 0,
          }}>
            {msg.role === 'user' ? 'U' : 'AF'}
          </div>

          {/* Bubble */}
          <div style={{
            maxWidth: '75%',
            padding: '10px 14px',
            background: msg.role === 'user'
              ? 'rgba(99,102,241,0.15)'
              : 'var(--os-surface)',
            border: '1px solid var(--os-border)',
            borderRadius: msg.role === 'user' ? '12px 4px 12px 12px' : '4px 12px 12px 12px',
            fontSize: '12px',
            lineHeight: '1.6',
            color: 'var(--os-text)',
          }}>
            {msg.agentName && msg.role === 'assistant' && (
              <div style={{ fontSize: '10px', color: '#ec4899', fontWeight: 600, marginBottom: '4px' }}>
                {msg.agentName}
              </div>
            )}
            <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
            {msg.duration && (
              <div style={{ fontSize: '9px', color: 'var(--os-dim)', marginTop: '4px' }}>
                {msg.duration.toFixed(1)}s
              </div>
            )}
          </div>
        </div>
      ))}

      {/* Loading indicator */}
      {loading && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
          <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: 'linear-gradient(135deg,#ec4899,#f43f5e)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, color: '#fff' }}>AF</div>
          <div style={{ padding: '10px 14px', background: 'var(--os-surface)', border: '1px solid var(--os-border)', borderRadius: '4px 12px 12px 12px', display: 'flex', gap: '4px', alignItems: 'center' }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                width: '6px', height: '6px', borderRadius: '50%', background: '#ec4899',
                animation: `bounce 1.2s ${i * 0.2}s infinite`,
              }} />
            ))}
          </div>
        </div>
      )}

      <div ref={bottomRef} />
      <style>{`@keyframes bounce{0%,80%,100%{transform:scale(0)}40%{transform:scale(1)}}`}</style>
    </div>
  )
}
